import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { Modal } from '../../../../../web/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../web/components/Select';
import { Button } from '../../../../../web/components-v2/Button';
import { Input } from '../../../../../web/components-v2/Input';
import { TextArea } from '../../../../../web/components-v2/TextArea';
import { PricingCalculationResponse } from '../types';
import { styleSheet } from './styles';
import {
	CONTRACT_TYPE_OPTIONS,
	ContractFormData,
	ContractType,
	CURRENT_PLAN_OPTIONS,
	MANAGER_OPTIONS,
	URGENCY_OPTIONS,
} from './types';
import { useContractGeneration } from './useContractGeneration';

// LocalStorage keys for caching rep names
const REP_FIRST_NAME_KEY = 'levitate_contractModal_repFirstName';
const REP_LAST_NAME_KEY = 'levitate_contractModal_repLastName';

// Helper functions for localStorage
const getCachedRepFirstName = (): string => {
	try {
		return localStorage.getItem(REP_FIRST_NAME_KEY) || '';
	} catch {
		return '';
	}
};

const getCachedRepLastName = (): string => {
	try {
		return localStorage.getItem(REP_LAST_NAME_KEY) || '';
	} catch {
		return '';
	}
};

const cacheRepFirstName = (value: string): void => {
	try {
		localStorage.setItem(REP_FIRST_NAME_KEY, value);
	} catch {
		// Silently fail if localStorage is not available
	}
};

const cacheRepLastName = (value: string): void => {
	try {
		localStorage.setItem(REP_LAST_NAME_KEY, value);
	} catch {
		// Silently fail if localStorage is not available
	}
};

interface IProps {
	isOpen: boolean;
	onRequestClose: () => void;
	contractType: ContractType;
	pricingData?: PricingCalculationResponse;
	isSuccessTeam?: boolean;
}

export function GenerateContractModal({ isOpen, onRequestClose, contractType, pricingData, isSuccessTeam }: IProps) {
	const isNewClient = contractType === 'new';

	// Shared fields
	const [customerName, setCustomerName] = React.useState('');
	const [customerTitle, setCustomerTitle] = React.useState('');
	const [customerEmail, setCustomerEmail] = React.useState('');
	const [customerAddress, setCustomerAddress] = React.useState('');
	const [companyName, setCompanyName] = React.useState('');
	const [repFirstName, setRepFirstName] = React.useState(getCachedRepFirstName());
	const [repLastName, setRepLastName] = React.useState(getCachedRepLastName());
	const [managerEmail, setManagerEmail] = React.useState<string>(MANAGER_OPTIONS[0].email);
	const [urgency, setUrgency] = React.useState('Send to prospect immediately');
	const [hipaaCompliance, setHipaaCompliance] = React.useState(false);
	const [additionalNotes, setAdditionalNotes] = React.useState('');

	// Current client specific fields
	const [adminLink, setAdminLink] = React.useState('');
	const [currentLevitatePlan, setCurrentLevitatePlan] = React.useState('');
	const [renewalDate, setRenewalDate] = React.useState('');
	const [monthsLeft, setMonthsLeft] = React.useState<number>(0);
	const [billingCycle, setBillingCycle] = React.useState('Annual');
	const [contractTypeValue, setContractTypeValue] = React.useState('');
	const [totalPriceDueToday, setTotalPriceDueToday] = React.useState<number>(0);
	const [totalPriceUponRenewal, setTotalPriceUponRenewal] = React.useState<number>(0);
	const [discountBreakdown, setDiscountBreakdown] = React.useState<number>(0);
	const [fbAdsChecked, setFbAdsChecked] = React.useState(false);
	const [fbPremiumChecked, setFbPremiumChecked] = React.useState(false);
	const [gLSAChecked, setGLSAChecked] = React.useState(false);
	const [customContentChecked, setCustomContentChecked] = React.useState(false);
	const [customContentNonprofitChecked, setCustomContentNonprofitChecked] = React.useState(false);
	const [handwritten, setHandwritten] = React.useState(false);
	const [pendingCancel, setPendingCancel] = React.useState('no');

	// Success state
	const [generatedFileId, setGeneratedFileId] = React.useState<string | null>(null);
	const [generatedFileName, setGeneratedFileName] = React.useState<string | null>(null);

	const contractMutation = useContractGeneration();

	// Calculate months left when renewal date changes
	React.useEffect(() => {
		if (renewalDate) {
			const today = new Date();
			const renewal = new Date(renewalDate);
			const diffTime = renewal.getTime() - today.getTime();
			const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
			setMonthsLeft(Math.max(0, diffMonths));
		}
	}, [renewalDate]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validate required fields
		if (!customerName || !customerTitle || !customerEmail || !customerAddress || !companyName) {
			toast.error('Please fill in all customer information fields');
			return;
		}

		if (!repFirstName || !repLastName) {
			toast.error('Please fill in rep name');
			return;
		}

		if (!managerEmail || !urgency) {
			toast.error('Please select manager and urgency');
			return;
		}

		// Validate current client specific fields
		if (!isNewClient) {
			if (!adminLink || !currentLevitatePlan || !contractTypeValue) {
				toast.error('Please fill in all required current client fields');
				return;
			}
			// For Success team, only Total Price Upon Renewal is required
			// For Sales team, both Total Price Due Today and Total Price Upon Renewal are required
			if (isSuccessTeam) {
				if (typeof totalPriceUponRenewal !== 'number') {
					toast.error('Please enter Total Price Upon Renewal');
					return;
				}
			} else {
				if (typeof totalPriceDueToday !== 'number' || typeof totalPriceUponRenewal !== 'number') {
					toast.error('Please enter all required pricing fields');
					return;
				}
			}
		}

		const formData: ContractFormData = {
			customerName,
			customerTitle,
			customerEmail,
			customerAddress,
			companyName,
			repFirstName,
			repLastName,
			managerEmail,
			urgency,
			hipaaCompliance,
			additionalNotes,
			isSuccessTeam,
		};

		// Add current client specific data
		if (!isNewClient) {
			formData.adminLink = adminLink;
			formData.currentLevitatePlan = currentLevitatePlan;
			formData.renewalDate = renewalDate;
			formData.monthsLeft = monthsLeft;
			formData.billingCycle = billingCycle;
			formData.contractTypeValue = contractTypeValue;
			formData.totalPriceDueToday = totalPriceDueToday;
			formData.totalPriceUponRenewal = totalPriceUponRenewal;
			formData.discountBreakdown = discountBreakdown;
			formData.fbAdsChecked = fbAdsChecked;
			formData.fbPremiumChecked = fbPremiumChecked;
			formData.gLSAChecked = gLSAChecked;
			formData.customContentChecked = customContentChecked;
			formData.customContentNonprofitChecked = customContentNonprofitChecked;
			formData.handwritten = handwritten;
			formData.pendingCancel = pendingCancel;
		}

		// Cache rep names for future use
		if (repFirstName) {
			cacheRepFirstName(repFirstName);
		}
		if (repLastName) {
			cacheRepLastName(repLastName);
		}

		contractMutation.mutate(
			{ ...formData, contractType, pricingData },
			{
				onError: error => {
					toast.error(`Failed to generate contract: ${error.message}`);
				},
				onSuccess: data => {
					if (data.success) {
						setGeneratedFileId(data.fileId || null);
						setGeneratedFileName(data.fileName || 'Contract');
						toast.success('Contract generated successfully!');
					} else {
						toast.error(`Failed to generate contract: ${data.error || 'Unknown error'}`);
					}
				},
			}
		);
	};

	const handleClose = () => {
		// Reset all form fields
		setCustomerName('');
		setCustomerTitle('');
		setCustomerEmail('');
		setCustomerAddress('');
		setCompanyName('');
		setRepFirstName('');
		setRepLastName('');
		setManagerEmail(MANAGER_OPTIONS[0].email);
		setUrgency('Send to prospect immediately');
		setHipaaCompliance(false);
		setAdditionalNotes('');
		setAdminLink('');
		setCurrentLevitatePlan('');
		setRenewalDate('');
		setMonthsLeft(0);
		setBillingCycle('Annual');
		setContractTypeValue('');
		setTotalPriceDueToday(0);
		setTotalPriceUponRenewal(0);
		setDiscountBreakdown(0);
		setFbAdsChecked(false);
		setFbPremiumChecked(false);
		setGLSAChecked(false);
		setCustomContentChecked(false);
		setCustomContentNonprofitChecked(false);
		setHandwritten(false);
		setPendingCancel('no');
		setGeneratedFileId(null);
		setGeneratedFileName(null);
		onRequestClose();
	};

	const formatCurrency = (value: number | undefined): string => {
		const numericValue = value ?? 0;
		return numericValue.toLocaleString('en-US', {
			maximumFractionDigits: 2,
			minimumFractionDigits: 2,
		});
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={handleClose} useDefaultHeader={true}>
			<div className={css(styleSheet.container)}>
				<h2 className={css(styleSheet.title)}>Generate {isNewClient ? 'New Client' : 'Current Client'} Contract</h2>

				{/* Price Summary for New Clients */}
				{isNewClient && pricingData && (
					<div className={css(styleSheet.priceSummary)}>
						<div>
							<strong>Plan:</strong> {pricingData.planType || 'N/A'}
						</div>
						<div>
							<strong>Users:</strong> {pricingData.numUsers || 'N/A'}
						</div>
						<div>
							<strong>Annual Total:</strong> $
							{formatCurrency((pricingData.totalBeforeTaxes ?? 0) + (pricingData.taxAmount ?? 0))}
						</div>
						<div>
							<strong>Billing Cycle:</strong> {pricingData.billingCycle || 'Annual'}
						</div>
						{/* Show discount if present */}
						{pricingData.discountPercentage && pricingData.discountPercentage > 0 && (
							<div>
								<strong>Discount:</strong> {pricingData.discountPercentage}% ($
								{formatCurrency(pricingData.discountAmount)})
							</div>
						)}
						{/* Show add-ons if any selected */}
						{(pricingData.fbAdsChecked ||
							pricingData.fbAdsPremiumChecked ||
							pricingData.gLSAChecked ||
							pricingData.customContentChecked ||
							pricingData.handwritten) && (
							<div>
								<strong>Add-ons:</strong>{' '}
								{[
									pricingData.fbAdsChecked && `Meta Ads ($${formatCurrency(pricingData.facebookAdsFee)})`,
									pricingData.fbAdsPremiumChecked && 'Meta Premium ($2,988)',
									pricingData.gLSAChecked && `Google LSA ($${formatCurrency(pricingData.googleLSAFee)})`,
									pricingData.customContentChecked &&
										`Custom Content ($${formatCurrency(pricingData.customContentFee)})`,
									pricingData.customContentNonprofit && 'Custom Content (Non-Profit) ($0.00)',
									pricingData.handwritten && 'Handwritten Cards',
								]
									.filter(Boolean)
									.join(', ')}
							</div>
						)}
					</div>
				)}

				{!generatedFileId && (
					<form onSubmit={handleSubmit}>
						{/* Customer Information Section */}
						<div className={css(styleSheet.formSection)}>
							<h3 className={css(styleSheet.sectionTitle)}>
								Customer Information <span className={css(styleSheet.required)}>*</span>
							</h3>
							<div className={css(styleSheet.formGroup)}>
								<label className={css(styleSheet.label)} htmlFor='customerName'>
									Customer Name <span className={css(styleSheet.required)}>*</span>
								</label>
								<Input
									styles={[styleSheet.input]}
									id='customerName'
									onChange={e => setCustomerName(e.target.value)}
									required
									type='text'
									value={customerName}
								/>
							</div>

							<div className={css(styleSheet.formGroup)}>
								<label className={css(styleSheet.label)} htmlFor='customerTitle'>
									Title <span className={css(styleSheet.required)}>*</span>
								</label>
								<Input
									styles={[styleSheet.input]}
									id='customerTitle'
									onChange={e => setCustomerTitle(e.target.value)}
									placeholder='e.g., CEO, Owner, Partner'
									required
									type='text'
									value={customerTitle}
								/>
							</div>

							<div className={css(styleSheet.formGroup)}>
								<label className={css(styleSheet.label)} htmlFor='customerEmail'>
									Customer Email <span className={css(styleSheet.required)}>*</span>
								</label>
								<Input
									styles={[styleSheet.input]}
									id='customerEmail'
									onChange={e => setCustomerEmail(e.target.value)}
									required
									type='email'
									value={customerEmail}
								/>
							</div>

							<div className={css(styleSheet.formGroup)}>
								<label className={css(styleSheet.label)} htmlFor='customerAddress'>
									Customer Address <span className={css(styleSheet.required)}>*</span>
								</label>
								<TextArea
									styles={[styleSheet.textarea]}
									id='customerAddress'
									onChange={e => setCustomerAddress(e.target.value)}
									required
									value={customerAddress}
								/>
							</div>

							<div className={css(styleSheet.formGroup)}>
								<label className={css(styleSheet.label)} htmlFor='companyName'>
									Company Name <span className={css(styleSheet.required)}>*</span>
								</label>
								<Input
									styles={[styleSheet.input]}
									id='companyName'
									onChange={e => setCompanyName(e.target.value)}
									required
									type='text'
									value={companyName}
								/>
							</div>
						</div>

						{/* Rep Information Section */}
						<div className={css(styleSheet.formSection)}>
							<h3 className={css(styleSheet.sectionTitle)}>
								Rep Information <span className={css(styleSheet.required)}>*</span>
							</h3>
							<div className={css(styleSheet.formRow)}>
								<div className={css(styleSheet.formGroup)}>
									<label className={css(styleSheet.label)} htmlFor='repFirstName'>
										First Name <span className={css(styleSheet.required)}>*</span>
									</label>
									<Input
										styles={[styleSheet.input]}
										id='repFirstName'
										onChange={e => setRepFirstName(e.target.value)}
										required
										type='text'
										value={repFirstName}
									/>
								</div>

								<div className={css(styleSheet.formGroup)}>
									<label className={css(styleSheet.label)} htmlFor='repLastName'>
										Last Name <span className={css(styleSheet.required)}>*</span>
									</label>
									<Input
										styles={[styleSheet.input]}
										id='repLastName'
										onChange={e => setRepLastName(e.target.value)}
										required
										type='text'
										value={repLastName}
									/>
								</div>
							</div>

							{repFirstName && repLastName && (
								<div className={css(styleSheet.formGroup)}>
									<label className={css(styleSheet.label)}>Rep Email (auto-generated)</label>
									<Input
										styles={[styleSheet.input]}
										disabled
										type='text'
										value={`${repFirstName.toLowerCase()}.${repLastName.toLowerCase()}@levitateapp.com`}
									/>
								</div>
							)}
						</div>

						{/* Manager & Urgency Section */}
						<div className={css(styleSheet.formSection)}>
							<h3 className={css(styleSheet.sectionTitle)}>
								Manager & Urgency <span className={css(styleSheet.required)}>*</span>
							</h3>
							<div className={css(styleSheet.formGroup)}>
								<label className={css(styleSheet.label)} htmlFor='managerEmail'>
									Manager <span className={css(styleSheet.required)}>*</span>
								</label>
								<Select value={managerEmail} onValueChange={setManagerEmail}>
									<SelectTrigger styles={styleSheet.select}>
										<SelectValue placeholder='Select Manager' />
									</SelectTrigger>
									<SelectContent>
										{MANAGER_OPTIONS.filter(manager => !manager.successTeamOnly || isSuccessTeam).map(manager => (
											<SelectItem key={manager.email} value={manager.email}>
												{manager.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className={css(styleSheet.formGroup)}>
								<label className={css(styleSheet.label)} htmlFor='urgency'>
									Urgency <span className={css(styleSheet.required)}>*</span>
								</label>
								<Select value={urgency} onValueChange={setUrgency}>
									<SelectTrigger styles={styleSheet.select}>
										<SelectValue placeholder='Select Urgency' />
									</SelectTrigger>
									<SelectContent>
										{URGENCY_OPTIONS.map(option => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>

						{/* Current Client Specific Fields */}
						{!isNewClient && (
							<>
								<div className={css(styleSheet.formSection)}>
									<h3 className={css(styleSheet.sectionTitle)}>
										Current Client Details <span className={css(styleSheet.required)}>*</span>
									</h3>
									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='adminLink'>
											Admin Link <span className={css(styleSheet.required)}>*</span>
										</label>
										<Input
											styles={[styleSheet.input]}
											id='adminLink'
											onChange={e => setAdminLink(e.target.value)}
											placeholder='https://...'
											required
											type='url'
											value={adminLink}
										/>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='currentLevitatePlan'>
											Current Levitate Plan <span className={css(styleSheet.required)}>*</span>
										</label>
										<Select value={currentLevitatePlan} onValueChange={setCurrentLevitatePlan}>
											<SelectTrigger styles={styleSheet.select}>
												<SelectValue placeholder='Select Current Plan' />
											</SelectTrigger>
											<SelectContent>
												{CURRENT_PLAN_OPTIONS.map(option => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									<div className={css(styleSheet.formRow)}>
										<div className={css(styleSheet.formGroup)}>
											<label className={css(styleSheet.label)} htmlFor='renewalDate'>
												Renewal Date
											</label>
											<Input
												styles={[styleSheet.input]}
												id='renewalDate'
												onChange={e => setRenewalDate(e.target.value)}
												type='date'
												value={renewalDate}
											/>
										</div>

										<div className={css(styleSheet.formGroup)}>
											<label className={css(styleSheet.label)} htmlFor='monthsLeft'>
												How many months are they paying for
											</label>
											<Input
												styles={[styleSheet.input]}
												id='monthsLeft'
												onChange={e => setMonthsLeft(Number(e.target.value))}
												type='number'
												value={String(monthsLeft)}
											/>
										</div>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='contractType'>
											Contract Type <span className={css(styleSheet.required)}>*</span>
										</label>
										<Select value={contractTypeValue} onValueChange={setContractTypeValue}>
											<SelectTrigger styles={styleSheet.select}>
												<SelectValue placeholder='Select Contract Type' />
											</SelectTrigger>
											<SelectContent>
												{CONTRACT_TYPE_OPTIONS.filter(option => !option.successTeamOnly || isSuccessTeam).map(
													option => (
														<SelectItem key={option.value} value={option.value}>
															{option.label}
														</SelectItem>
													)
												)}
											</SelectContent>
										</Select>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='billingCycle'>
											Billing Cycle <span className={css(styleSheet.required)}>*</span>
										</label>
										<Select value={billingCycle} onValueChange={setBillingCycle}>
											<SelectTrigger styles={styleSheet.select}>
												<SelectValue placeholder='Select Billing Cycle' />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value='Annual'>Annual</SelectItem>
												<SelectItem value='Quarterly'>Quarterly</SelectItem>
												{isSuccessTeam && <SelectItem value='Monthly'>Monthly</SelectItem>}
											</SelectContent>
										</Select>
									</div>

									<div className={css(styleSheet.formRow)}>
										<div className={css(styleSheet.formGroup)}>
											<label className={css(styleSheet.label)} htmlFor='totalPriceDueToday'>
												Total Price Due Today {!isSuccessTeam && <span className={css(styleSheet.required)}>*</span>}
											</label>
											<Input
												styles={[styleSheet.input]}
												id='totalPriceDueToday'
												onChange={e => setTotalPriceDueToday(Number(e.target.value))}
												placeholder='Amount due today'
												required={!isSuccessTeam}
												type='number'
												value={String(totalPriceDueToday)}
											/>
										</div>

										<div className={css(styleSheet.formGroup)}>
											<label className={css(styleSheet.label)} htmlFor='totalPriceUponRenewal'>
												Total Price Upon Renewal <span className={css(styleSheet.required)}>*</span>
											</label>
											<Input
												styles={[styleSheet.input]}
												id='totalPriceUponRenewal'
												onChange={e => setTotalPriceUponRenewal(Number(e.target.value))}
												placeholder='Levitate core + website'
												required
												type='number'
												value={String(totalPriceUponRenewal)}
											/>
										</div>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='discountBreakdown'>
											Discount Breakdown
										</label>
										<Input
											styles={[styleSheet.input]}
											id='discountBreakdown'
											onChange={e => setDiscountBreakdown(Number(e.target.value))}
											placeholder='Discount amount'
											type='number'
											value={String(discountBreakdown)}
										/>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='pendingCancel'>
											Pending Cancel
										</label>
										<Select value={pendingCancel} onValueChange={setPendingCancel}>
											<SelectTrigger styles={styleSheet.select}>
												<SelectValue placeholder='Select Option' />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value='no'>No</SelectItem>
												<SelectItem value='yes'>Yes</SelectItem>
											</SelectContent>
										</Select>
									</div>
								</div>

								{/* Add-ons for Current Clients */}
								<div className={css(styleSheet.formSection)}>
									<h3 className={css(styleSheet.sectionTitle)}>Add-ons</h3>
									<div className={css(styleSheet.checkboxGrid)}>
										<Checkbox
											checked={fbAdsChecked}
											onChange={e => setFbAdsChecked((e.target as HTMLInputElement).checked)}
										>
											<span className={css(styleSheet.checkboxLabel)}>Meta Ads</span>
										</Checkbox>
										<Checkbox
											checked={fbPremiumChecked}
											onChange={e => setFbPremiumChecked((e.target as HTMLInputElement).checked)}
										>
											<span className={css(styleSheet.checkboxLabel)}>Meta Ads Premium</span>
										</Checkbox>
										<Checkbox
											checked={gLSAChecked}
											onChange={e => setGLSAChecked((e.target as HTMLInputElement).checked)}
										>
											<span className={css(styleSheet.checkboxLabel)}>Google LSAs</span>
										</Checkbox>
										<Checkbox
											checked={customContentChecked}
											onChange={e => setCustomContentChecked((e.target as HTMLInputElement).checked)}
										>
											<span className={css(styleSheet.checkboxLabel)}>Custom Content</span>
										</Checkbox>
										<Checkbox
											checked={customContentNonprofitChecked}
											onChange={e => setCustomContentNonprofitChecked((e.target as HTMLInputElement).checked)}
										>
											<span className={css(styleSheet.checkboxLabel)}>Custom Content (Non-Profit)</span>
										</Checkbox>
										<Checkbox
											checked={handwritten}
											onChange={e => setHandwritten((e.target as HTMLInputElement).checked)}
										>
											<span className={css(styleSheet.checkboxLabel)}>Handwritten Cards</span>
										</Checkbox>
									</div>
								</div>
							</>
						)}

						{/* Additional Options Section */}
						<div className={css(styleSheet.formSection)}>
							<h3 className={css(styleSheet.sectionTitle)}>Additional Options</h3>
							<Checkbox
								checked={hipaaCompliance}
								onChange={e => setHipaaCompliance((e.target as HTMLInputElement).checked)}
							>
								<span className={css(styleSheet.checkboxLabel)}>HIPAA Compliance</span>
							</Checkbox>

							<div className={css(styleSheet.formGroup, styleSheet.formGroupWithTopMargin)}>
								<label className={css(styleSheet.label)} htmlFor='additionalNotes'>
									Additional Notes
								</label>
								<TextArea
									styles={[styleSheet.textarea]}
									id='additionalNotes'
									onChange={e => setAdditionalNotes(e.target.value)}
									placeholder='Enter any additional notes or special instructions...'
									value={additionalNotes}
								/>
							</div>
						</div>

						{/* Action Buttons */}
						<div className={css(styleSheet.buttonRow)}>
							<Button disabled={contractMutation.isPending} onClick={handleClose} type='button' variant='secondary'>
								Cancel
							</Button>
							<Button disabled={contractMutation.isPending} isLoading={contractMutation.isPending} type='submit'>
								Generate Contract
							</Button>
						</div>
					</form>
				)}

				{/* Success Section */}
				{generatedFileId && (
					<div className={css(styleSheet.successSection)}>
						<h3 className={css(styleSheet.successTitle)}>✅ Contract Generated Successfully!</h3>
						<p className={css(styleSheet.fileName)}>{generatedFileName}</p>
						<p className={css(styleSheet.successMessage)}>
							An email has been sent with the contract details and link to the designated recipients.
						</p>
						<Button onClick={handleClose} type='button'>
							Close
						</Button>
					</div>
				)}
			</div>
		</Modal>
	);
}
